import { useEffect, useMemo, useState } from "react";

export interface ReportTableColumn<T> {
  key: keyof T;
  header: string;
  align?: "left" | "center" | "right";
}

interface ReportPointsTableProps<T extends { id: string }> {
  data: T[];
  columns: ReportTableColumn<T>[];
  isDarkMode?: boolean;
  pageSize?: number;
  emptyText?: string;
  onRowClick?: (row: T) => void;
  isRowSelected?: (row: T) => boolean;
  focusRowId?: string | null;
}

function ReportPointsTable<T extends { id: string }>({
  data,
  columns,
  isDarkMode = false,
  pageSize = 50,
  emptyText = "Sin datos",
  onRowClick,
  isRowSelected,
  focusRowId,
}: ReportPointsTableProps<T>) {
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!focusRowId) return;
    const index = data.findIndex((row) => row.id === focusRowId);
    if (index >= 0) setPage(Math.floor(index / pageSize));
  }, [focusRowId, data, pageSize]);

  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);

  const rows = useMemo(
    () => data.slice(currentPage * pageSize, currentPage * pageSize + pageSize),
    [data, currentPage, pageSize],
  );

  const headBg = "bg-primary-500 text-white";
  const cellText = isDarkMode ? "text-white" : "text-gray-900";

  return (
    <div className="w-full flex flex-col gap-3">
      <div className="w-full overflow-auto rounded-lg shadow max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className={`${headBg} sticky top-0 z-10`}>
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  className="p-3 font-bold whitespace-nowrap bg-primary-500"
                  style={{ textAlign: col.align || "left" }}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={`p-4 text-center ${cellText} ${isDarkMode ? "bg-[#232323]" : "bg-white"}`}
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row, index) => {
                const isSelected = isRowSelected ? isRowSelected(row) : false;
                return (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={onRowClick ? "cursor-pointer transition-colors" : undefined}
                  style={{
                    backgroundColor: isSelected
                      ? isDarkMode
                        ? "#1e3a5f"
                        : "#dbeafe"
                      : isDarkMode
                        ? index % 2 === 0
                          ? "#232323"
                          : "#1C1C1C"
                        : index % 2 === 0
                          ? "#f5f5f5"
                          : "#ffffff",
                    boxShadow: isSelected ? "inset 3px 0 0 #2563eb" : undefined,
                  }}
                >
                  {columns.map((col) => (
                    <td
                      key={String(col.key)}
                      className={`p-2 whitespace-nowrap ${cellText}`}
                      style={{ textAlign: col.align || "left" }}
                    >
                      {String(row[col.key] ?? "")}
                    </td>
                  ))}
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data.length > pageSize && (
        <div className="flex items-center justify-center gap-3 text-sm">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={currentPage === 0}
            className="px-3 py-1 rounded bg-primary-500 text-white disabled:opacity-40"
          >
            Anterior
          </button>
          <span className={cellText}>
            Página {currentPage + 1} de {totalPages} ({data.length} registros)
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={currentPage >= totalPages - 1}
            className="px-3 py-1 rounded bg-primary-500 text-white disabled:opacity-40"
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}

export default ReportPointsTable;
